import {
  buildUpdateMatParam,
  UpdateMatParam,
  CreateMatParam,
  buildCreateMatParam,
  MatFilterObj,
} from 'domains/matDomain';
import { createModel } from '@rematch/core';
import { Paged } from 'domains/appDomain';
import { ListMatParam, Material, PartialMat } from 'domains/matDomain';
import type { RootModel } from 'models';
import platformMatService from 'services/matService';
import { baseReducers, BaseState } from './baseReducers';
import { FILTER_NOT_EXISTS } from 'utils/constants';

type MatBaseSelectState = {
  visible: boolean;
  data?: Paged<Material>;
  currentPage: number;
};

interface MatState extends BaseState {
  data?: Paged<Material>;
  item?: PartialMat;
  currentPage: number;
  pageSize: number;
  matBaseSelect: MatBaseSelectState;
  noMarkupCount: number;
  filterObj: MatFilterObj;
}

export const mat = createModel<RootModel>()({
  state: {
    data: undefined,
    item: undefined,
    currentPage: 1,
    pageSize: 20,
    matBaseSelect: {
      visible: false,
      data: undefined,
      currentPage: 1,
    },
    noMarkupCount: 0,
    filterObj: {},
  } as MatState,
  reducers: {
    ...baseReducers<MatState>(),
    setMatBaseSelectVisible: (
      state: MatState,
      matBaseSelectVisible: boolean
    ) => {
      state.matBaseSelect.visible = matBaseSelectVisible;
      return state;
    },
    setMatBaseSelectData: (state: MatState, data: Paged<Material>) => {
      state.matBaseSelect.data = data;
      return state;
    },
    setMatBaseSelectCurrentPage: (state: MatState, currentPage: number) => {
      state.matBaseSelect.currentPage = currentPage;
      return state;
    },
    reset: (state: MatState) => {
      state.filterObj = {};
      return state;
    },
  },
  effects: (dispatch) => {
    const { mat } = dispatch;
    return {
      async getList(payload: ListMatParam): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await platformMatService.queryMats(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          mat.setState({
            data: resp,
            currentPage,
          });
        } else {
          mat.setState({
            data: resp,
          });
        }
      },

      async getListForBaseSelect(payload: ListMatParam): Promise<void> {
        let currentPage = payload.page.currentPage;
        let resp = await platformMatService.queryBaseMats(payload);
        if (resp.totalPages > 0 && currentPage > resp.totalPages) {
          // out of range
          currentPage = resp.totalPages;
          mat.setMatBaseSelectData(resp);
          mat.setMatBaseSelectCurrentPage(currentPage);
        } else {
          mat.setMatBaseSelectData(resp);
        }
      },

      async saveItem(payload: PartialMat, rootState): Promise<void> {
        if (payload.id) {
          // update existing
          const updateParam: UpdateMatParam = buildUpdateMatParam(payload);
          await platformMatService.updateMat(updateParam);
        } else {
          // create new
          const createParam: CreateMatParam = buildCreateMatParam(payload);
          await platformMatService.createMat(createParam);
        }
        await mat.getList({
          page: {
            currentPage: rootState.mat.currentPage,
            pageSize: rootState.mat.pageSize,
          },
          query: {
            ...rootState.mat.filterObj,
          },
        });
        await mat.getNoMarkupCount();
      },

      async createFromBase(payload: number, rootState): Promise<void> {
        await platformMatService.createFromBase(payload);

        await mat.getList({
          page: {
            currentPage: rootState.mat.currentPage,
            pageSize: rootState.mat.pageSize,
          },
          query: {
            ...rootState.mat.filterObj,
          },
        });
        await mat.getNoMarkupCount();
      },

      async removeItem(payload: number, rootState): Promise<void> {
        await platformMatService.removeMat(payload);
        await mat.getList({
          page: {
            currentPage: rootState.mat.currentPage,
            pageSize: rootState.mat.pageSize,
          },
          query: {
            ...rootState.mat.filterObj,
          },
        });
        await mat.getNoMarkupCount();
      },

      async removeAll(_, rootStat): Promise<void> {
        await platformMatService.removeAllMats();
        await mat.getList({
          page: {
            currentPage: rootStat.mat.currentPage,
            pageSize: rootStat.mat.pageSize,
          },
          query: {
            ...rootStat.mat.filterObj,
          },
        });
        await mat.getNoMarkupCount();
      },

      async getNoMarkupCount(): Promise<void> {
        const query: ListMatParam = {
          page: {
            pageSize: 1,
            currentPage: 1,
          },
          query: {
            active: true,
            markupPct: FILTER_NOT_EXISTS,
          },
        };
        const resp = await platformMatService.queryMats(query);

        mat.setState({
          noMarkupCount: resp.totalElements,
        });
      },

      async saveFilter(payload: MatFilterObj): Promise<void> {
        mat.setState({
          currentPage: 1,
          filterObj: payload,
        });
      },

      async removeFilter(): Promise<void> {
        mat.clearFilter();
      },
    };
  },
});
